import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import BakeasyHeaderLabel from '../../components/lables/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import {
  BakeasyChip,
  BakeasyChipEdit,
  BakeasyChipVariance
} from '../../components/common/BakeasyChip';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import {
  downloadActiveOrders,
  fetchActiveOrders,
  ordersState
} from '../../redux_store/slices/ordersSlice';
import { ORDER_LEGENDS_COLORS, UNAUTHORIZED } from '../../constants/Constants';
import { authActions } from '../../redux_store/slices/authSlice';
import {
  BakeasyButtonExport,
  BakeasyButtonFilter,
  BakeasyButtonPrint
} from '../../components/buttons/BakeasyButton';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { formatDateTime } from '../../utils/DateUtils';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { activeOrderTableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject, preparePayload } from '../../utils/commonUtils';
import BakeasyLegendRow from '../../components/common/BakeasyLegendRow';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const ActiveOrders: FC = (): ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();

  const tableRef = React.useRef<any>();
  let myContext = React.useContext(AppContext);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [activeOrderList, setActiveOrderList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const [count, setCount] = React.useState<number>(0);
  const [tFilter, setTFilter] = React.useState<any>({});

  let { error, activeOrdersvalue, rowsPerPage, pageNo, filter } = useAppSelector(ordersState);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload = await preparePayload(
      { limit: rowsPerPage, page: newPage + 1, sortBy: 'createdDate', sortDirection: -1 },
      filter,
      search,
      newPage
    );
    await dispatch(fetchActiveOrders(payload));
    // await fetchData(fetchActiveOrders, payload);

  };

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (activeOrdersvalue?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = activeOrdersvalue?.data?.map((order: any) => {
        return {
          id: order?._id,
          orderId: order?.orderId,
          onOrderIdClick: () => {
            if (order?.status === 'Shipped') {
              navigate('/shippingdetails', { state: { id: order?._id } });
            } else {
              navigate('/orderDetails', { state: { id: order?._id, orderId: order?.orderId } });
            }
          },
          storeName: order?.storeName,
          totalPrice:
            order?.totalAmount || order?.totalAmount === 0
              ? parseFloat(order?.totalAmount)?.toFixed(2)
              : parseFloat('0')?.toFixed(2),
          orderDate: order?.orderDate ? formatDateTime(order?.orderDate) : '',
          requestedDeliveryDate: order?.requestedDeliveryDate,
          // ? formatDateTime(order?.requestedDeliveryDate)
          // : '',
          variance: <BakeasyChipVariance label={`${order?.variance}`} />,
          status: <BakeasyChip label={order?.status} />,
          actions: [
            <BakeasyChipEdit
              key={order?._id}
              onClick={(event: any) => {
                event.stopPropagation();
                // navigate('/orderDetails', {
                //   state: { id: order?._id, orderId: order?.orderId }
                // });
                if (order?.status === 'Shipped') {
                  navigate('/shippingdetails', { state: { id: order?._id } });
                } else {
                  navigate('/orderDetails', { state: { id: order?._id, orderId: order?.orderId } });
                }
              }}
            />
          ]
        };
      });

      setActiveOrderList(filteredData);
      setCount(activeOrdersvalue?.totalCount);
    }
  }, [activeOrdersvalue]);

  function openFilterOrderConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'activeOrder-filter',
      open: true,
      data: filter,
      title: t('ACTIVE_ORDER_FILTER'),
      onNoClick: (event: any) => {},
      onYesClick: async (event: any, param: any) => {
        setTFilter(param);
        refreshPageData(0, search, param);
      }
    });
  }
  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function activeOrdersUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('ACTIVE_ORDERS')} />

          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <div className="tableOptions">
                <BakeasyButtonPrint
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadActiveOrders({ ...filter, downloadType: 'pdf' }));
                  }}
                />

                <BakeasyButtonExport
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    dispatch(downloadActiveOrders({ ...filter, downloadType: 'csv' }));
                  }}
                />

                <BakeasyButtonFilter
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openFilterOrderConfirmDialog();
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />
          <BakeasyLegendRow colorLabels={ORDER_LEGENDS_COLORS} />

          <Grid>
            {activeOrderList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={activeOrderTableHeaders}
                tableData={activeOrderList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return activeOrdersUI();
};
